import { Component, EventEmitter, forwardRef, Input, Output } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { ControlValueAccessorBase } from '@shared/bases/control-value-accessor.base';
import { SharedModule } from '@shared/modules/shared.module';

@Component({
	selector: 'app-select',
	imports: [SharedModule],
	templateUrl: './select.component.html',
	styleUrl: './select.component.scss',
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => SelectComponent),
			multi: true,
		},
	],
})
export class SelectComponent<T = string | number | boolean> extends ControlValueAccessorBase<T> {
	@Input() options: { label: string; value: T; icon?: string; hasPayroll?: boolean; payrollStatus?: string }[] = [];

	@Input() size: 'small' | 'medium' | 'large' = 'medium';
	@Input() placeholder = ''; // Empty = translated default (common.placeholders.select)
	@Input() disabled = false;
	@Input() error = false;
	@Input() errorMessage = ''; // Empty = translated default (common.messages.requiredField)
	@Input() label?: string;
	@Input() showIcon = false;
	@Input() loading = false;
	@Input() filter = false; // Enable search/filter functionality
	@Input() filterPlaceholder = ''; // Search placeholder (empty = common.placeholders.search)
	/** Shows a red asterisk after the label and marks the control as aria-required. */
	@Input() required = false;
	@Input() appendTo: 'body' | null = 'body'; // Allow configuring appendTo behavior
	/** Text typed in the search box (with filter on), for callers that search on the server */
	@Output() filterChange = new EventEmitter<string>();

	getPayrollIconColor(payrollStatus?: string): string {
		if (!payrollStatus) return '#22c55e';

		switch (payrollStatus) {
			case 'generated':
				return '#ef4444';
			case 'regenerated':
				return '#f97316';
			case 'sent':
				return '#eab308';
			case 'signed':
				return '#22c55e';
			default:
				return '#22c55e';
		}
	}

	selectId = `select-${Math.random().toString(36).substring(2, 11)}`;
	isDropdownOpen = false;

	set selectedOption(value: any) {
		this.value = value;
		this.notifyValueChange(value);
	}

	get selectedOption(): any {
		return this.value;
	}

	onDropdownOpen(): void {
		this.isDropdownOpen = true;
	}

	onDropdownClose(): void {
		this.isDropdownOpen = false;
		this.markAsTouched();
	}

	onBlur(): void {
		this.markAsTouched();
	}

	/**
	 * Sets the disabled state of the select.
	 */
	override setDisabledState(isDisabled: boolean): void {
		this.disabled = isDisabled;
	}
}
